import React from 'react';
import { Tag, Tooltip, Progress, Space } from 'antd';
import { 
  CrownOutlined, 
  RocketOutlined, 
  StarOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined 
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { PLANS } from '../../services/planService';
import usePlan from '../../hooks/usePlan';

/**
 * Composant badge pour afficher le plan utilisateur
 */
const PlanBadge = ({ 
  showProgress = false, 
  showUpgradeLink = true, 
  size = 'default' 
}) => {
  const { 
    currentPlan, 
    planName, 
    isTrialPlan, 
    trialMessagesUsed, 
    trialMaxMessages,
    loading 
  } = usePlan();

  // Configuration des icônes par plan
  const getPlanIcon = (plan) => {
    const icons = {
      [PLANS.DECOUVERTE]: <InfoCircleOutlined />,
      [PLANS.ESSENTIEL]: <RocketOutlined />,
      [PLANS.CROISSANCE]: <StarOutlined />,
      [PLANS.EXCELLENCE]: <CrownOutlined />
    };
    return icons[plan] || <InfoCircleOutlined />;
  };

  // Configuration des couleurs par plan
  const getPlanColor = (plan) => {
    const colors = {
      [PLANS.DECOUVERTE]: 'default',
      [PLANS.ESSENTIEL]: 'blue',
      [PLANS.CROISSANCE]: 'cyan',
      [PLANS.EXCELLENCE]: 'purple'
    };
    return colors[plan] || 'default';
  };

  // Calculer le pourcentage d'utilisation pour les plans d'essai
  const getUsagePercentage = () => {
    if (!isTrialPlan || trialMaxMessages === 0) return 0;
    return Math.round((trialMessagesUsed / trialMaxMessages) * 100);
  };

  // Contenu du tooltip avec détails du plan
  const getTooltipContent = () => {
    if (!currentPlan) return 'Chargement...';

    return (
      <div className="space-y-2">
        <div>
          <strong>{planName}</strong>
          {isTrialPlan && <div className="text-orange-300">Version d'essai</div>}
        </div>
        
        {isTrialPlan && (
          <div>
            <div className="text-sm">
              Messages utilisés: {trialMessagesUsed} / {trialMaxMessages}
            </div>
            {showProgress && (
              <Progress 
                percent={getUsagePercentage()}
                size="small"
                status={getUsagePercentage() >= 90 ? "exception" : "normal"}
                className="mt-1"
              />
            )}
          </div>
        )}
        
        {showUpgradeLink && currentPlan !== PLANS.EXCELLENCE && (
          <div className="text-blue-300 text-sm">
            Cliquez pour voir les options de mise à niveau
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Tag icon={<InfoCircleOutlined />} className="animate-pulse">
        Chargement...
      </Tag>
    );
  }

  if (!currentPlan) {
    return null;
  }

  const BadgeContent = () => (
    <Space size="small">
      <Tag 
        color={getPlanColor(currentPlan)} 
        icon={getPlanIcon(currentPlan)}
        size={size}
      >
        {planName}
      </Tag>
      
      {isTrialPlan && (
        <Tag 
          color="orange" 
          icon={<ClockCircleOutlined />}
          size={size}
        >
          Essai
        </Tag>
      )}
      
      {showProgress && isTrialPlan && (
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {trialMessagesUsed}/{trialMaxMessages}
          </span>
          <Progress 
            percent={getUsagePercentage()}
            size="small"
            showInfo={false}
            status={getUsagePercentage() >= 90 ? "exception" : "normal"}
            style={{ width: '60px' }}
          />
        </div>
      )}
    </Space>
  );

  // Si on peut faire des upgrades et que ce n'est pas le plan Excellence
  if (showUpgradeLink && currentPlan !== PLANS.EXCELLENCE) {
    return (
      <Tooltip title={getTooltipContent()} placement="bottom">
        <Link to="/pricing" className="inline-block">
          <BadgeContent />
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={getTooltipContent()} placement="bottom">
      <div className="inline-block">
        <BadgeContent />
      </div>
    </Tooltip>
  );
};

export default PlanBadge; 